import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Search, Eye, Trash2, X, MessageSquare, Lightbulb, AlertTriangle, Calendar, MapPin, Users,
  CheckCircle2, Ban, Clock3, Tag as TagIcon, Heart, RefreshCw, ArrowUpDown, AlertCircle,
  CalendarClock, ExternalLink, RotateCcw, Megaphone, FileText, Loader2,
} from "lucide-react";
import moment from "moment";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AdminLoginAPi, deleteWithToken, fetchWithToken } from "api/ApiHandler";
import Sidebar1 from "components/Sidebar1";
import DEfulatImg from "../../Assests/Images/DefulatLogo.png";
import { useDialog, DialogFrame, DialogSection } from "../Admindashboard/ui";
import { FONT_HEADING, fromNow, handleAuthError, EmptyState } from "../userManagement/shared";

// ─── Helpers ────────────────────────────────────────────────────────────────

const getAvatarUrl = (userId, { forceDefaultForServiceProvider = false } = {}) => {
  if (forceDefaultForServiceProvider && userId?.role === "service_provider") return DEfulatImg;
  const path = userId?.imagePath || userId?.image;
  if (!path || path === "image") return DEfulatImg;
  return path;
};

const getAuthorName = (userId) => {
  if (!userId) return "Unknown";
  if (userId.businessName) return userId.businessName;
  const name = `${userId.firstName || ""} ${userId.lastName || ""}`.trim();
  return name || "Unknown";
};

const roleLabel = (role) => (role === "service_provider" ? "Service provider" : role === "customer" || !role ? "Customer" : role.replace(/_/g, " "));

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  const m = moment.utc(value);
  if (!m.isValid()) return "-";
  return m.format(withTime ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY");
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const m = moment.utc(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const eventStatusKey = (status) => {
  const s = (status || "pending").toLowerCase();
  return s === "approved" || s === "live" ? "live" : s === "rejected" ? "rejected" : "pending";
};

const EVENT_STATUS = {
  pending: { label: "Pending", badge: "bg-[#FEF3C7] text-[#B45309]", dot: "#F59E0B" },
  live: { label: "Live", badge: "bg-[#DCFCE7] text-[#16A34A]", dot: "#16A34A" },
  rejected: { label: "Rejected", badge: "bg-[#FDECEA] text-[#EA352B]", dot: "#EA352B" },
};

const POST_TYPES = {
  tip: { label: "Tip", Icon: Lightbulb, badge: "bg-[#DBEAFE] text-[#2563EB]" },
  alert: { label: "Alert", Icon: AlertTriangle, badge: "bg-[#FFEDD5] text-[#EA580C]" },
  post: { label: "Post", Icon: FileText, badge: "bg-[#F3F4F6] text-[#4B5563]" },
};
const postTypeMeta = (t) => POST_TYPES[(t || "post").toLowerCase()] || { ...POST_TYPES.post, label: t };

const getEventImage = (e) => e?.image || e?.eventImage || e?.coverImage;

// Items rendered per scroll step of the feed
const FEED_BATCH = 10;

const clamp = (lines) => ({ display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" });

const POST_SORTS = {
  newest: { label: "Newest first", fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  oldest: { label: "Oldest first", fn: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) },
  liked: { label: "Most liked", fn: (a, b) => (b.likes?.length || 0) - (a.likes?.length || 0) },
};
const EVENT_SORTS = {
  soonest: { label: "Event date (soonest)", fn: (a, b) => new Date(a.eventDateTime) - new Date(b.eventDateTime) },
  newest: { label: "Recently created", fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  popular: { label: "Most joined", fn: (a, b) => (b.going?.length || 0) - (a.going?.length || 0) },
};

// ─── Page ───────────────────────────────────────────────────────────────────

export default function CommunityManagement() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("posts");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [postType, setPostType] = useState("all");
  const [eventStatus, setEventStatus] = useState("all");
  const [postSort, setPostSort] = useState("newest");
  const [eventSort, setEventSort] = useState("soonest");
  const [visibleCount, setVisibleCount] = useState(FEED_BATCH);

  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // { type: 'post' | 'event', id, title }
  const [busy, setBusy] = useState(null); // `${action}:${id}` while a request runs

  const [posts, setPosts] = useState([]);
  const [isPostsLoading, setIsPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState("");

  const [events, setEvents] = useState([]);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");

  const mainRef = useRef(null);

  const loadPosts = async () => {
    try {
      setIsPostsLoading(true);
      setPostsError("");
      const data = await fetchWithToken("/admin/get-all-posts");
      setPosts(Array.isArray(data?.posts) ? data.posts : []);
    } catch (error) {
      console.error("Fetch posts error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setPostsError("Unable to load posts.");
    } finally {
      setIsPostsLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      setIsEventsLoading(true);
      setEventsError("");
      const data = await fetchWithToken("/admin/get-all-events");
      setEvents(Array.isArray(data?.events) ? data.events : []);
    } catch (error) {
      console.error("Fetch events error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setEventsError("Unable to load events.");
    } finally {
      setIsEventsLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    loadEvents();
  }, []);

  useEffect(() => {
    setVisibleCount(FEED_BATCH);
  }, [searchTerm, dateFilter, activeTab, postType, eventStatus, postSort, eventSort]);

  // ── Actions (same endpoints as before) ──
  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const approveEvent = (id) => run(`approve:${id}`, async () => {
    try {
      await AdminLoginAPi("/admin/approve-event", { id });
      toast.success("Event approved and is now live.");
      setSelectedEvent((prev) => (prev && prev._id === id ? { ...prev, status: "approved" } : prev));
      loadEvents();
    } catch (error) {
      console.error("Approve event error:", error.response?.data || error);
      handleAuthError(error);
      // error toast is already shown by the global axios interceptor
    }
  });

  const rejectEvent = (id) => run(`reject:${id}`, async () => {
    try {
      await AdminLoginAPi("/admin/reject-event", { id });
      toast.success("Event rejected.");
      setSelectedEvent((prev) => (prev && prev._id === id ? { ...prev, status: "rejected" } : prev));
      loadEvents();
    } catch (error) {
      console.error("Reject event error:", error.response?.data || error);
      handleAuthError(error);
    }
  });

  const deleteEvent = async (id) => {
    try {
      await deleteWithToken(`/admin/delete-event/${id}`);
      toast.success("Event deleted.");
      setSelectedEvent((prev) => (prev && prev._id === id ? null : prev));
      loadEvents();
    } catch (error) {
      console.error("Delete event error:", error.response?.data || error);
      handleAuthError(error);
    }
  };

  const deletePost = async (id) => {
    try {
      await deleteWithToken(`/admin/delete-post/${id}`);
      toast.success("Post deleted.");
      setSelectedPost((prev) => (prev && prev._id === id ? null : prev));
      loadPosts();
    } catch (error) {
      console.error("Delete post error:", error.response?.data || error);
      handleAuthError(error);
    }
  };

  const confirmDelete = () => {
    if (!confirmTarget) return;
    const { type, id } = confirmTarget;
    run(`delete:${id}`, async () => {
      if (type === "post") await deletePost(id);
      else await deleteEvent(id);
      setConfirmTarget(null);
    });
  };

  // ── Stats ──
  const postStats = useMemo(() => {
    const weekAgo = moment().subtract(7, "days");
    return {
      total: posts.length,
      tips: posts.filter((p) => (p.postType || "").toLowerCase() === "tip").length,
      alerts: posts.filter((p) => (p.postType || "").toLowerCase() === "alert").length,
      likes: posts.reduce((s, p) => s + (p.likes?.length || 0), 0),
      thisWeek: posts.filter((p) => moment.utc(p.createdAt).isAfter(weekAgo)).length,
    };
  }, [posts]);

  const eventCounts = useMemo(() => {
    const c = { all: events.length, pending: 0, live: 0, rejected: 0, upcoming: 0, going: 0 };
    events.forEach((e) => {
      c[eventStatusKey(e.status)] += 1;
      c.going += e.going?.length || 0;
      if (eventStatusKey(e.status) === "live" && moment(e.eventDateTime).isAfter(moment())) c.upcoming += 1;
    });
    return c;
  }, [events]);

  const postTypeCounts = useMemo(() => {
    const c = { all: posts.length };
    posts.forEach((p) => {
      const k = (p.postType || "post").toLowerCase();
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [posts]);

  // Distinct people who posted or created events, most recent first
  const members = useMemo(() => {
    const seen = new Map();
    [...posts, ...events]
      .filter((x) => x.userId && typeof x.userId === "object")
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .forEach((x) => {
        const id = x.userId._id || getAuthorName(x.userId);
        if (!seen.has(id)) seen.set(id, x.userId);
      });
    return [...seen.values()];
  }, [posts, events]);

  const topContributors = useMemo(() => {
    const byUser = new Map();
    posts.forEach((p) => {
      if (!p.userId) return;
      const id = p.userId._id || getAuthorName(p.userId);
      const row = byUser.get(id) || { user: p.userId, posts: 0, likes: 0 };
      row.posts += 1;
      row.likes += p.likes?.length || 0;
      byUser.set(id, row);
    });
    return [...byUser.values()].sort((a, b) => b.posts - a.posts || b.likes - a.likes).slice(0, 5);
  }, [posts]);

  const trendingTags = useMemo(() => {
    const counts = {};
    posts.forEach((p) => (p.tags || []).forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [posts]);

  const pendingEvents = useMemo(
    () => events.filter((e) => eventStatusKey(e.status) === "pending").sort(EVENT_SORTS.soonest.fn),
    [events],
  );

  const upcomingEvents = useMemo(
    () => events
      .filter((e) => eventStatusKey(e.status) === "live" && moment(e.eventDateTime).isAfter(moment()))
      .sort(EVENT_SORTS.soonest.fn)
      .slice(0, 4),
    [events],
  );

  // ── Filtering ──
  const filteredPosts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return posts
      .filter((p) => {
        const matchesTerm =
          !term ||
          (p.description || "").toLowerCase().includes(term) ||
          getAuthorName(p.userId).toLowerCase().includes(term) ||
          (p.tags || []).some((t) => String(t).toLowerCase().includes(term));
        const matchesDate = !dateFilter || toDateInputValue(p.createdAt) === dateFilter;
        const matchesType = postType === "all" || (p.postType || "post").toLowerCase() === postType;
        return matchesTerm && matchesDate && matchesType;
      })
      .sort(POST_SORTS[postSort].fn);
  }, [posts, searchTerm, dateFilter, postType, postSort]);

  const filteredEvents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return events
      .filter((e) => {
        const matchesTerm =
          !term ||
          (e.eventName || "").toLowerCase().includes(term) ||
          (e.location?.text || "").toLowerCase().includes(term) ||
          getAuthorName(e.userId).toLowerCase().includes(term);
        const matchesDate = !dateFilter || toDateInputValue(e.eventDateTime) === dateFilter;
        const matchesStatus = eventStatus === "all" || eventStatusKey(e.status) === eventStatus;
        return matchesTerm && matchesDate && matchesStatus;
      })
      .sort(EVENT_SORTS[eventSort].fn);
  }, [events, searchTerm, dateFilter, eventStatus, eventSort]);

  const isPosts = activeTab === "posts";
  const activeList = isPosts ? filteredPosts : filteredEvents;
  const isLoading = isPosts ? isPostsLoading : isEventsLoading;
  const loadError = isPosts ? postsError : eventsError;
  const currentItems = activeList.slice(0, visibleCount);
  const hasMore = visibleCount < activeList.length;
  const loadMore = useCallback(() => setVisibleCount((n) => n + FEED_BATCH), []);
  const hasFilters = searchTerm || dateFilter || (isPosts ? postType !== "all" : eventStatus !== "all");

  const clearFilters = () => {
    setSearchTerm("");
    setDateFilter("");
    setPostType("all");
    setEventStatus("all");
  };

  const refresh = () => (isPosts ? loadPosts() : loadEvents());

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchTerm("");
    setDateFilter("");
  };

  // Rail shortcuts: filter the feed and bring it back to the top
  const searchFor = (term) => {
    setSearchTerm(term);
    mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex h-screen bg-[#F0F2F5] overflow-hidden">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      <main ref={mainRef} className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-[1180px] mx-auto px-8 lg:px-6 md:px-4 sm:px-3 pt-6 pb-12">
          {/* Cover */}
          <section className="relative overflow-hidden rounded-3xl text-[#fff]" style={{ background: "linear-gradient(115deg, #15151B 0%, #2A1614 55%, #EA352B 150%)" }}>
            <div className="pointer-events-none absolute -right-16 -top-24 w-80 h-80 rounded-full bg-[#EA352B] opacity-25" />
            <div className="pointer-events-none absolute right-40 -bottom-24 w-56 h-56 rounded-full border-[28px] border-[#ffffff0d]" />
            <div className="relative flex items-end justify-between gap-6 px-8 py-8 md:px-6 md:flex-col md:items-start sm:px-5 sm:py-6">
              <div className="min-w-0">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ffffff1a] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#ffffffcc]">
                  <Megaphone size={12} /> Moderation
                </span>
                <h1 className="m-0 mt-3 text-[34px] sm:text-[26px] font-medium leading-tight" style={FONT_HEADING}>Fuelingo Community</h1>
                <p className="m-0 mt-1.5 max-w-[520px] text-sm text-[#ffffffb3]">
                  Everything people share in the app. Keep the feed clean and approve events before they go live.
                </p>
                <div className="flex items-center gap-3 mt-5">
                  <div className="flex">
                    {members.slice(0, 6).map((u, i) => (
                      <Avatar key={u._id || i} src={getAvatarUrl(u)} size={30} className={`border-2 border-[#1F1A1C] ${i ? "-ml-2.5" : ""}`} />
                    ))}
                  </div>
                  <span className="text-xs text-[#ffffffb3]">
                    {isPostsLoading || isEventsLoading ? "Loading members…" : `${members.length} ${members.length === 1 ? "member" : "members"} contributing`}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 shrink-0 md:w-full">
                <HeroStat label="Posts" value={isPostsLoading ? "…" : postStats.total} sub={`${postStats.thisWeek} this week`} />
                <HeroStat label="Events" value={isEventsLoading ? "…" : events.length} sub={`${eventCounts.upcoming} upcoming`} />
                <HeroStat label="To review" value={isEventsLoading ? "…" : eventCounts.pending} sub={eventCounts.pending ? "Pending events" : "All clear"} highlight={eventCounts.pending > 0} />
              </div>
            </div>
          </section>

          {/* Sticky nav */}
          <div className="sticky top-0 z-20 py-3 bg-[#F0F2F5e6] backdrop-blur">
            <div className="flex items-center gap-3 rounded-2xl bg-[#fff] border border-[#E4E6EB] shadow-[0_1px_2px_rgba(0,0,0,0.06)] p-2 sm:flex-wrap">
              <div role="tablist" className="flex gap-1 sm:flex-1">
                <NavTab active={isPosts} onClick={() => switchTab("posts")} Icon={MessageSquare} label="Posts" count={isPostsLoading ? "…" : posts.length} />
                <NavTab active={!isPosts} onClick={() => switchTab("events")} Icon={Calendar} label="Events" count={isEventsLoading ? "…" : events.length} alert={eventCounts.pending > 0} />
              </div>

              <div className="relative flex-1 max-w-[380px] ml-auto sm:order-last sm:max-w-none sm:basis-full">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#65676B]" size={16} />
                {/* No `type` attr: styles/tailwind.css resets padding/border on typed inputs */}
                <input
                  role="searchbox"
                  enterKeyHint="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
                  placeholder={isPosts ? "Search posts, people or #tags" : "Search events, organizers or places"}
                  className="w-full pl-10 pr-9 py-2.5 bg-[#F0F2F5] border border-transparent rounded-full text-sm text-[#050505] placeholder-[#65676B] focus:outline-none focus:border-[#EA352B] focus:bg-[#fff] focus:ring-0 transition"
                />
                {searchTerm && (
                  <button type="button" aria-label="Clear search" onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#65676B] hover:text-[#111]">
                    <X size={15} />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={refresh}
                disabled={isLoading}
                title="Refresh"
                aria-label="Refresh"
                className="w-10 h-10 shrink-0 rounded-full bg-[#F0F2F5] text-[#050505] flex items-center justify-center hover:bg-[#E4E6EB] transition disabled:opacity-60"
              >
                <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          {/* Feed + right rail */}
          <div className="flex items-start gap-6 md:flex-col">
            <div className="flex-1 min-w-0 md:w-full">
              {/* Filter bar */}
              <div className="rounded-2xl bg-[#fff] border border-[#E4E6EB] shadow-[0_1px_2px_rgba(0,0,0,0.06)] px-4 py-3 mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {isPosts
                    ? Object.keys(postTypeCounts).map((k) => (
                      <Chip key={k} active={postType === k} onClick={() => setPostType(k)} label={k === "all" ? "All" : postTypeMeta(k).label} count={postTypeCounts[k]} />
                    ))
                    : ["all", "pending", "live", "rejected"].map((k) => (
                      <Chip
                        key={k}
                        active={eventStatus === k}
                        onClick={() => setEventStatus(k)}
                        label={k === "all" ? "All" : EVENT_STATUS[k].label}
                        count={eventCounts[k]}
                        dot={k === "all" ? null : EVENT_STATUS[k].dot}
                      />
                    ))}
                </div>

                <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-[#F0F2F5]">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      value={dateFilter ? moment(dateFilter) : null}
                      onChange={(v) => setDateFilter(v && v.isValid() ? v.format("YYYY-MM-DD") : "")}
                      format="DD MMM YYYY"
                      slotProps={{
                        field: { clearable: true, onClear: () => setDateFilter("") },
                        textField: {
                          size: "small",
                          placeholder: isPosts ? "Posted on" : "Event date",
                          sx: {
                            width: 180,
                            "& .MuiOutlinedInput-root": { borderRadius: "999px", backgroundColor: "#F0F2F5", fontSize: "13px", fontFamily: "'NeueMontreal', sans-serif" },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E4E6EB" },
                            "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#EA352B !important", borderWidth: "1px !important" },
                            "& .MuiInputBase-input": { padding: "8px 14px" },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>

                  <label className="relative inline-flex items-center">
                    <ArrowUpDown size={13} className="absolute left-3 text-[#65676B] pointer-events-none" />
                    <select
                      value={isPosts ? postSort : eventSort}
                      onChange={(e) => (isPosts ? setPostSort : setEventSort)(e.target.value)}
                      aria-label="Sort"
                      className="appearance-none bg-[#F0F2F5] border border-transparent rounded-full pl-8 pr-8 py-2 text-[13px] text-[#050505] focus:outline-none focus:border-[#EA352B] focus:ring-0 cursor-pointer"
                    >
                      {Object.entries(isPosts ? POST_SORTS : EVENT_SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                    </select>
                  </label>

                  <span className="ml-auto text-xs text-[#65676B]">
                    {!isLoading && <><b className="text-[#050505]">{activeList.length}</b> {isPosts ? (activeList.length === 1 ? "post" : "posts") : activeList.length === 1 ? "event" : "events"}</>}
                    {hasFilters && (
                      <button type="button" onClick={clearFilters} className="ml-3 font-semibold text-[#EA352B] hover:text-[#C92A21]">
                        Clear filters
                      </button>
                    )}
                  </span>
                </div>
              </div>

              {/* Feed */}
              {loadError ? (
                <div className="rounded-2xl bg-[#fff] border border-[#E4E6EB]">
                  <EmptyState
                    Icon={AlertCircle}
                    title={loadError}
                    text="Check your connection and try again."
                    action={<button type="button" onClick={refresh} className="mt-3 rounded-full bg-[#EA352B] text-[#fff] px-5 py-2 text-sm font-medium">Try again</button>}
                  />
                </div>
              ) : isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (isPosts ? <PostCardSkeleton key={i} /> : <EventRowSkeleton key={i} />))}
                </div>
              ) : currentItems.length === 0 ? (
                <div className="rounded-2xl bg-[#fff] border border-[#E4E6EB]">
                  <EmptyState
                    Icon={isPosts ? MessageSquare : Calendar}
                    title={isPosts ? "No posts found" : "No events found"}
                    text={hasFilters ? "Try a different search or filter." : isPosts ? "Posts from the app will appear here." : "Events created in the app will appear here."}
                    action={hasFilters && <button type="button" onClick={clearFilters} className="mt-3 rounded-full bg-[#F0F2F5] text-[#050505] px-5 py-2 text-sm font-medium hover:bg-[#E4E6EB]">Clear filters</button>}
                  />
                </div>
              ) : isPosts ? (
                <div className="space-y-4">
                  {currentItems.map((p) => (
                    <PostCard
                      key={p._id}
                      post={p}
                      onOpen={() => setSelectedPost(p)}
                      onDelete={() => setConfirmTarget({ type: "post", id: p._id, title: getAuthorName(p.userId) })}
                      onTag={(t) => searchFor(t)}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {currentItems.map((e) => (
                    <EventRow
                      key={e._id}
                      event={e}
                      busy={busy}
                      onOpen={() => setSelectedEvent(e)}
                      onApprove={() => approveEvent(e._id)}
                      onReject={() => rejectEvent(e._id)}
                      onDelete={() => setConfirmTarget({ type: "event", id: e._id, title: e.eventName })}
                    />
                  ))}
                </div>
              )}

              {!isLoading && !loadError && activeList.length > 0 && (
                <FeedEnd hasMore={hasMore} onLoadMore={loadMore} shown={currentItems.length} total={activeList.length} label={isPosts ? "posts" : "events"} />
              )}
            </div>

            {/* Right rail */}
            <aside className="w-[340px] shrink-0 sticky top-[84px] space-y-4 md:static md:w-full md:order-first">
              {isPosts ? (
                <>
                  <RailCard title="Overview">
                    <div className="grid grid-cols-2 gap-2">
                      <MiniTile Icon={MessageSquare} label="Total posts" value={postStats.total} loading={isPostsLoading} />
                      <MiniTile Icon={Clock3} label="Last 7 days" value={postStats.thisWeek} loading={isPostsLoading} />
                      <MiniTile Icon={Heart} label="Total likes" value={postStats.likes} loading={isPostsLoading} />
                      <MiniTile Icon={Users} label="Likes / post" value={postStats.total ? (postStats.likes / postStats.total).toFixed(1) : 0} loading={isPostsLoading} />
                    </div>
                    {!isPostsLoading && postStats.total > 0 && (
                      <TypeMix tips={postStats.tips} alerts={postStats.alerts} other={postStats.total - postStats.tips - postStats.alerts} />
                    )}
                  </RailCard>

                  <RailCard title="Top contributors" className="md:hidden">
                    {isPostsLoading ? <RailSkeleton /> : topContributors.length === 0 ? (
                      <p className="m-0 text-xs text-[#65676B]">No posts yet.</p>
                    ) : (
                      <ul className="m-0 p-0 list-none -mx-2">
                        {topContributors.map((c, i) => (
                          <li key={c.user._id || i}>
                            <button type="button" onClick={() => searchFor(getAuthorName(c.user))} className="w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-[#F2F2F2] transition">
                              <span className="relative">
                                <Avatar src={getAvatarUrl(c.user)} size={36} />
                                {i === 0 && <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#F59E0B] border-2 border-[#fff] text-[8px] font-bold text-[#fff] flex items-center justify-center">1</span>}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-semibold text-[#050505] truncate">{getAuthorName(c.user)}</span>
                                <span className="block text-[11px] text-[#65676B]">{c.posts} {c.posts === 1 ? "post" : "posts"} · {c.likes} {c.likes === 1 ? "like" : "likes"}</span>
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </RailCard>

                  {trendingTags.length > 0 && (
                    <RailCard title="Trending tags" className="md:hidden">
                      <div className="flex flex-wrap gap-1.5">
                        {trendingTags.map(([t, n]) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => searchFor(t)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#F0F2F5] px-3 py-1.5 text-xs font-medium text-[#050505] hover:bg-[#FDECEA] hover:text-[#EA352B] transition"
                          >
                            #{t} <span className="text-[10px] text-[#65676B]">{n}</span>
                          </button>
                        ))}
                      </div>
                    </RailCard>
                  )}
                </>
              ) : (
                <>
                  <RailCard
                    title="Needs review"
                    badge={eventCounts.pending > 0 ? eventCounts.pending : null}
                    action={eventCounts.pending > 5 && <button type="button" onClick={() => setEventStatus("pending")} className="text-xs font-semibold text-[#EA352B]">See all</button>}
                  >
                    {isEventsLoading ? <RailSkeleton /> : pendingEvents.length === 0 ? (
                      <div className="flex items-center gap-3 rounded-xl bg-[#F0FDF4] px-3 py-3">
                        <CheckCircle2 size={18} className="text-[#16A34A] shrink-0" />
                        <p className="m-0 text-xs text-[#166534]">You’re all caught up. No events waiting for approval.</p>
                      </div>
                    ) : (
                      <ul className="m-0 p-0 list-none -mx-2">
                        {pendingEvents.slice(0, 5).map((e) => (
                          <li key={e._id} className="flex items-center gap-2 rounded-xl px-2 py-2 hover:bg-[#F2F2F2] transition">
                            <button type="button" onClick={() => setSelectedEvent(e)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                              <MiniDate value={e.eventDateTime} />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-[#050505] truncate">{e.eventName || "Untitled event"}</span>
                                <span className="block text-[11px] text-[#65676B] truncate">by {getAuthorName(e.userId)}</span>
                              </span>
                            </button>
                            <IconBtn Icon={CheckCircle2} label="Approve" onClick={() => approveEvent(e._id)} success loading={busy === `approve:${e._id}`} disabled={!!busy} />
                            <IconBtn Icon={Ban} label="Reject" onClick={() => rejectEvent(e._id)} danger loading={busy === `reject:${e._id}`} disabled={!!busy} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </RailCard>

                  <RailCard title="Coming up" className="md:hidden">
                    {isEventsLoading ? <RailSkeleton /> : upcomingEvents.length === 0 ? (
                      <p className="m-0 text-xs text-[#65676B]">No upcoming live events.</p>
                    ) : (
                      <ul className="m-0 p-0 list-none -mx-2">
                        {upcomingEvents.map((e) => (
                          <li key={e._id}>
                            <button type="button" onClick={() => setSelectedEvent(e)} className="w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-[#F2F2F2] transition">
                              <MiniDate value={e.eventDateTime} />
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-semibold text-[#050505] truncate">{e.eventName || "Untitled event"}</span>
                                <span className="block text-[11px] text-[#65676B]">{fromNow(e.eventDateTime)} · {e.going?.length || 0} going</span>
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </RailCard>

                  <RailCard title="Overview" className="md:hidden">
                    <div className="grid grid-cols-2 gap-2">
                      <MiniTile Icon={CheckCircle2} label="Live" value={eventCounts.live} loading={isEventsLoading} />
                      <MiniTile Icon={CalendarClock} label="Upcoming" value={eventCounts.upcoming} loading={isEventsLoading} />
                      <MiniTile Icon={Ban} label="Rejected" value={eventCounts.rejected} loading={isEventsLoading} />
                      <MiniTile Icon={Users} label="People going" value={eventCounts.going} loading={isEventsLoading} />
                    </div>
                  </RailCard>
                </>
              )}
            </aside>
          </div>
        </div>
      </main>

      <PostDialog
        post={selectedPost}
        paused={!!confirmTarget}
        onClose={() => setSelectedPost(null)}
        onDelete={() => setConfirmTarget({ type: "post", id: selectedPost._id, title: getAuthorName(selectedPost.userId) })}
      />
      <EventDialog
        event={selectedEvent}
        paused={!!confirmTarget}
        busy={busy}
        onClose={() => setSelectedEvent(null)}
        onApprove={() => approveEvent(selectedEvent._id)}
        onReject={() => rejectEvent(selectedEvent._id)}
        onDelete={() => setConfirmTarget({ type: "event", id: selectedEvent._id, title: selectedEvent.eventName })}
      />
      <ConfirmDelete
        target={confirmTarget}
        busy={confirmTarget && busy === `delete:${confirmTarget.id}`}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

// ─── Page pieces ────────────────────────────────────────────────────────────

function HeroStat({ label, value, sub, highlight }) {
  return (
    <div className={`rounded-2xl px-4 py-3 min-w-[118px] sm:min-w-0 sm:px-3 backdrop-blur ${highlight ? "bg-[#EA352B] shadow-[0_10px_30px_rgba(234,53,43,0.35)]" : "bg-[#ffffff14] border border-[#ffffff1a]"}`}>
      <p className="m-0 text-[11px] font-medium uppercase tracking-wider text-[#ffffffb3]">{label}</p>
      <p className="m-0 mt-1 text-[26px] sm:text-xl font-semibold leading-none">{typeof value === "number" ? value.toLocaleString() : value}</p>
      <p className="m-0 mt-1.5 text-[11px] text-[#ffffffb3] truncate">{sub}</p>
    </div>
  );
}

function NavTab({ active, onClick, Icon, label, count, alert }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`relative inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition sm:flex-1 ${
        active ? "bg-[#FDECEA] text-[#EA352B]" : "text-[#65676B] hover:bg-[#F2F2F2]"
      }`}
    >
      <Icon size={17} /> {label}
      <span className={`rounded-full px-2 py-px text-[11px] ${active ? "bg-[#EA352B] text-[#fff]" : "bg-[#E4E6EB] text-[#050505]"}`}>{count}</span>
      {alert && !active && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#EA352B] animate-pulse" />}
    </button>
  );
}

function Chip({ active, onClick, label, count, dot }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition ${
        active ? "bg-[#1B1B22] text-[#fff]" : "bg-[#F0F2F5] text-[#050505] hover:bg-[#E4E6EB]"
      }`}
    >
      {dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />}
      {label}
      <span className={`text-[11px] ${active ? "text-[#ffffffb3]" : "text-[#65676B]"}`}>{count}</span>
    </button>
  );
}

function RailCard({ title, badge, action, className = "", children }) {
  return (
    <section className={`rounded-2xl bg-[#fff] border border-[#E4E6EB] shadow-[0_1px_2px_rgba(0,0,0,0.06)] p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="m-0 text-[15px] font-semibold text-[#050505]">{title}</h3>
        {badge != null && <span className="rounded-full bg-[#EA352B] px-2 py-px text-[11px] font-semibold text-[#fff]">{badge}</span>}
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {children}
    </section>
  );
}

function MiniTile({ Icon, label, value, loading }) {
  return (
    <div className="rounded-xl bg-[#F7F8FA] px-3 py-2.5">
      <span className="flex items-center gap-1.5 text-[11px] text-[#65676B]"><Icon size={12} className="text-[#EA352B]" /> {label}</span>
      {loading ? (
        <div className="h-5 w-10 mt-1.5 rounded bg-[#E4E6EB] animate-pulse" />
      ) : (
        <p className="m-0 mt-1 text-lg font-semibold text-[#050505]">{typeof value === "number" ? value.toLocaleString() : value}</p>
      )}
    </div>
  );
}

// Stacked bar: share of tips / alerts / regular posts
function TypeMix({ tips, alerts, other }) {
  const total = tips + alerts + other || 1;
  const parts = [
    { label: "Tips", value: tips, color: "#2563EB" },
    { label: "Alerts", value: alerts, color: "#EA580C" },
    { label: "Posts", value: other, color: "#9CA3AF" },
  ];
  return (
    <div className="mt-3">
      <div className="flex h-2 rounded-full overflow-hidden bg-[#F0F2F5]">
        {parts.map((p) => p.value > 0 && <span key={p.label} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} />)}
      </div>
      <div className="flex gap-3 mt-2">
        {parts.map((p) => (
          <span key={p.label} className="inline-flex items-center gap-1.5 text-[11px] text-[#65676B]">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} /> {p.label} {Math.round((p.value / total) * 100)}%
          </span>
        ))}
      </div>
    </div>
  );
}

function MiniDate({ value }) {
  const m = moment(value);
  const valid = m.isValid();
  return (
    <span className="shrink-0 w-11 rounded-xl overflow-hidden text-center border border-[#E4E6EB] bg-[#fff]">
      <span className="block bg-[#EA352B] text-[#fff] text-[9px] font-semibold uppercase tracking-wider py-0.5">{valid ? m.format("MMM") : "—"}</span>
      <span className="block text-base font-semibold text-[#050505] leading-none py-1.5">{valid ? m.format("D") : "?"}</span>
    </span>
  );
}

function RailSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#F0F2F5] animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-32 bg-[#F0F2F5] rounded animate-pulse" />
            <div className="h-2.5 w-20 bg-[#F0F2F5] rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Avatar({ src, size = 36, className = "" }) {
  return (
    <img
      src={src}
      onError={(e) => { e.currentTarget.src = DEfulatImg; }}
      alt=""
      className={`rounded-full object-cover shrink-0 bg-[#F3F3F6] ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

function TypeBadge({ type }) {
  const m = postTypeMeta(type);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${m.badge}`}>
      <m.Icon size={11} /> {m.label}
    </span>
  );
}

function EventStatusBadge({ status }) {
  const m = EVENT_STATUS[eventStatusKey(status)];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${m.badge}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.dot }} /> {m.label}
    </span>
  );
}

function PostImage({ src, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return <img src={src} alt="" onError={() => setFailed(true)} className={`object-cover ${className}`} />;
}

function ExpandableText({ text, lines = 5 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span className="text-[#9CA3AF] italic">No text</span>;
  const isLong = text.length > 320 || text.split("\n").length > lines;
  return (
    <>
      <span style={expanded || !isLong ? undefined : clamp(lines)} className="whitespace-pre-line block">{text}</span>
      {isLong && (
        <button
          type="button"
          onClick={(ev) => { ev.stopPropagation(); setExpanded((v) => !v); }}
          className="mt-1 text-[14px] font-semibold text-[#65676B] hover:underline"
        >
          {expanded ? "See less" : "See more"}
        </button>
      )}
    </>
  );
}

function FeedAction({ Icon, label, onClick, danger, success, loading, disabled }) {
  const tone = danger ? "hover:text-[#EA352B]" : success ? "hover:text-[#16A34A]" : "hover:text-[#111]";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg py-2 text-[13px] font-semibold text-[#65676B] hover:bg-[#F2F2F2] transition disabled:opacity-50 ${tone}`}
    >
      {loading ? <Loader2 size={17} className="animate-spin" /> : <Icon size={17} />} {label}
    </button>
  );
}

function PostCard({ post, onOpen, onDelete, onTag }) {
  const likes = post.likes?.length || 0;
  return (
    <article className="bg-[#fff] rounded-2xl border border-[#E4E6EB] shadow-[0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] transition-shadow">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 pt-3">
        <Avatar src={getAvatarUrl(post.userId)} size={40} />
        <div className="min-w-0 flex-1">
          <button type="button" onClick={onOpen} className="block max-w-full text-left text-[15px] font-semibold text-[#050505] truncate hover:underline">
            {getAuthorName(post.userId)}
          </button>
          <p className="m-0 text-[12px] text-[#65676B]" title={formatDate(post.createdAt, true)}>
            {fromNow(post.createdAt)} · {roleLabel(post.userId?.role)}
          </p>
        </div>
        <TypeBadge type={post.postType} />
        <IconBtn Icon={Trash2} label="Delete post" onClick={onDelete} danger />
      </div>

      {/* Text */}
      <div className="px-4 pt-2.5 pb-3 text-[15px] text-[#050505] leading-[1.45]">
        <ExpandableText text={post.description} />
        {post.tags?.length > 0 && (
          <p className="m-0 mt-1.5 flex flex-wrap gap-x-2 text-[14px] font-medium text-[#EA352B]">
            {post.tags.map((t) => (
              <button key={t} type="button" onClick={() => onTag?.(t)} className="hover:underline">#{t}</button>
            ))}
          </p>
        )}
      </div>

      {/* Image, full width like a feed photo */}
      {post.image && (
        <button type="button" onClick={onOpen} className="block w-full bg-[#F0F2F5] border-y border-[#E4E6EB]">
          <PostImage src={post.image} className="w-full max-h-[560px]" />
        </button>
      )}

      {/* Reactions summary */}
      <div className="flex items-center justify-between px-4 py-2.5 text-[13px] text-[#65676B]">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-[18px] h-[18px] rounded-full bg-[#EA352B] flex items-center justify-center">
            <Heart size={10} className="text-[#fff] fill-[#fff]" />
          </span>
          {likes}
        </span>
        {post.tags?.length > 0 && <span>{post.tags.length} {post.tags.length === 1 ? "tag" : "tags"}</span>}
      </div>

      {/* Actions */}
      <div className="flex gap-1 mx-3 py-1 border-t border-[#E4E6EB]">
        <FeedAction Icon={Eye} label="View" onClick={onOpen} />
        <FeedAction Icon={Trash2} label="Delete" onClick={onDelete} danger />
      </div>
    </article>
  );
}

function IconBtn({ Icon, label, onClick, danger, success, loading, disabled }) {
  const tone = danger
    ? "text-[#EA352B] hover:bg-[#FDECEA]"
    : success
      ? "text-[#16A34A] hover:bg-[#DCFCE7]"
      : "text-[#374151] hover:bg-[#F3F3F6]";
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled || loading}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition disabled:opacity-50 ${tone}`}
    >
      {loading ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />}
    </button>
  );
}

function DateTile({ value, large }) {
  const m = moment(value);
  const valid = m.isValid();
  return (
    <div className={`shrink-0 rounded-2xl overflow-hidden text-center border border-[#EEEEF1] bg-[#fff] ${large ? "w-20" : "w-14"}`}>
      <div className={`bg-[#EA352B] text-[#fff] font-semibold uppercase tracking-wider ${large ? "text-[11px] py-1" : "text-[9px] py-0.5"}`}>
        {valid ? m.format("MMM") : "—"}
      </div>
      <div className={`font-semibold text-[#111] leading-none ${large ? "text-3xl pt-2" : "text-xl pt-1.5"}`}>{valid ? m.format("D") : "?"}</div>
      <div className={`text-[#9CA3AF] ${large ? "text-[11px] pb-2 pt-1" : "text-[9px] pb-1.5 pt-0.5"}`}>{valid ? m.format("ddd") : ""}</div>
    </div>
  );
}

function EventRow({ event: e, busy, onOpen, onApprove, onReject, onDelete }) {
  const key = eventStatusKey(e.status);
  const when = moment(e.eventDateTime);
  const past = when.isBefore(moment());
  const image = getEventImage(e);
  return (
    <article className="bg-[#fff] rounded-2xl border border-[#E4E6EB] shadow-[0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] transition-shadow">
      {/* Organizer header */}
      <div className="flex items-center gap-2.5 px-4 py-3">
        <Avatar src={getAvatarUrl(e.userId, { forceDefaultForServiceProvider: true })} size={40} />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[15px] text-[#050505] truncate">
            <b className="font-semibold">{getAuthorName(e.userId)}</b> <span className="text-[#65676B]">created an event</span>
          </p>
          <p className="m-0 text-[12px] text-[#65676B]">{fromNow(e.createdAt)}</p>
        </div>
        <EventStatusBadge status={e.status} />
      </div>

      {/* Cover */}
      <button type="button" onClick={onOpen} className="relative block w-full text-left">
        {image ? (
          <div className="bg-[#F0F2F5]" style={{ aspectRatio: "16 / 8" }}>
            <PostImage src={image} className="w-full h-full" />
          </div>
        ) : (
          <div className="relative flex items-center justify-center overflow-hidden" style={{ aspectRatio: "16 / 6", background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 60%, #EA352B 140%)" }}>
            <div className="pointer-events-none absolute -right-12 -top-16 w-52 h-52 rounded-full bg-[#EA352B] opacity-20" />
            <DateTile value={e.eventDateTime} large />
          </div>
        )}
        {past && key === "live" && (
          <span className="absolute top-3 left-3 rounded-full bg-[#000000a6] text-[#fff] px-2.5 py-1 text-[11px] font-semibold">Ended</span>
        )}
      </button>

      {/* Details */}
      <button type="button" onClick={onOpen} className="block w-full text-left px-4 pt-3 pb-2">
        <p className="m-0 text-[13px] font-semibold uppercase tracking-wide text-[#EA352B]">
          {when.isValid() ? `${when.format("ddd, DD MMM YYYY")} at ${when.format("hh:mm A")}` : "Date not set"}
        </p>
        <p className="m-0 mt-0.5 text-[17px] font-semibold text-[#050505] leading-snug hover:underline">{e.eventName || "Untitled event"}</p>
        <p className="m-0 mt-1 text-[14px] text-[#65676B] flex items-center gap-1 min-w-0">
          <MapPin size={14} className="shrink-0" /> <span className="truncate">{e.location?.text || "No location"}</span>
        </p>
        {e.description && <p className="m-0 mt-2 text-[14px] text-[#374151] leading-relaxed" style={clamp(2)}>{e.description}</p>}
      </button>

      <div className="flex items-center gap-1.5 px-4 pb-2.5 text-[13px] text-[#65676B]">
        <Users size={14} /> {e.going?.length || 0} going
      </div>

      {key === "pending" && (
        <div className="mx-4 mb-2 flex items-center gap-2 rounded-lg bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2 text-[12px] text-[#78350F]">
          <Megaphone size={14} className="text-[#B45309] shrink-0" /> Waiting for approval, not visible in the app yet.
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-1 mx-3 py-1 border-t border-[#E4E6EB]">
        {key !== "live" && <FeedAction Icon={CheckCircle2} label="Approve" onClick={onApprove} success loading={busy === `approve:${e._id}`} disabled={!!busy} />}
        {key === "pending" && <FeedAction Icon={Ban} label="Reject" onClick={onReject} danger loading={busy === `reject:${e._id}`} disabled={!!busy} />}
        <FeedAction Icon={Eye} label="View" onClick={onOpen} />
        <FeedAction Icon={Trash2} label="Delete" onClick={onDelete} danger disabled={!!busy} />
      </div>
    </article>
  );
}

// Sentinel at the bottom of the feed: loads the next batch as it scrolls into view
function FeedEnd({ hasMore, onLoadMore, shown, total, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!hasMore || !ref.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) onLoadMore(); },
      { rootMargin: "400px 0px" },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasMore, shown, onLoadMore]);

  return (
    <div ref={ref} className="mt-5 flex items-center justify-center gap-2 py-4 text-[13px] text-[#65676B]">
      {hasMore ? (
        <><Loader2 size={16} className="animate-spin text-[#EA352B]" /> Loading more {label}… ({shown} of {total})</>
      ) : (
        <><CheckCircle2 size={16} className="text-[#16A34A]" /> You’re all caught up · {total} {label}</>
      )}
    </div>
  );
}

function PostCardSkeleton() {
  return (
    <div className="bg-[#fff] rounded-2xl border border-[#E4E6EB] p-4 space-y-3">
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-full bg-[#F0F2F5] animate-pulse" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-32 bg-[#F0F2F5] rounded animate-pulse" />
          <div className="h-2.5 w-20 bg-[#F0F2F5] rounded animate-pulse" />
        </div>
      </div>
      <div className="h-3 w-full bg-[#F0F2F5] rounded animate-pulse" />
      <div className="h-3 w-4/5 bg-[#F0F2F5] rounded animate-pulse" />
      <div className="h-56 w-full bg-[#F0F2F5] rounded-lg animate-pulse" />
    </div>
  );
}

function EventRowSkeleton() {
  return (
    <div className="bg-[#fff] rounded-2xl border border-[#E4E6EB] overflow-hidden">
      <div className="flex items-center gap-2.5 p-4">
        <div className="w-10 h-10 rounded-full bg-[#F0F2F5] animate-pulse" />
        <div className="h-3 w-48 bg-[#F0F2F5] rounded animate-pulse" />
      </div>
      <div className="h-48 w-full bg-[#F0F2F5] animate-pulse" />
      <div className="p-4 space-y-2">
        <div className="h-3 w-40 bg-[#F0F2F5] rounded animate-pulse" />
        <div className="h-4 w-64 max-w-full bg-[#F0F2F5] rounded animate-pulse" />
      </div>
    </div>
  );
}

// ─── Dialogs ────────────────────────────────────────────────────────────────

function DialogHeader({ children, onClose, closeRef }) {
  return (
    <div className="relative shrink-0 px-6 pt-6 pb-5 sm:px-4 text-[#fff] overflow-hidden" style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 60%, #EA352B 140%)" }}>
      <div className="pointer-events-none absolute -right-12 -top-16 w-52 h-52 rounded-full bg-[#EA352B] opacity-20" />
      <button
        ref={closeRef}
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute z-10 top-4 right-4 w-9 h-9 rounded-xl bg-[#ffffff1f] hover:bg-[#ffffff33] flex items-center justify-center transition"
      >
        <X size={18} className="text-[#fff]" />
      </button>
      <div className="relative pr-10">{children}</div>
    </div>
  );
}

function DialogFooter({ children }) {
  return <div className="shrink-0 flex gap-3 flex-wrap justify-end px-6 py-4 sm:px-4 bg-[#fff] border-t border-[#EEEEF1]">{children}</div>;
}

function ActionBtn({ Icon, label, onClick, variant = "neutral", loading, disabled }) {
  const styles = {
    danger: "bg-[#EA352B] text-[#fff] hover:bg-[#D42F26]",
    success: "bg-[#16A34A] text-[#fff] hover:bg-[#15803D]",
    outline: "border border-[#EEEEF1] text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B]",
    neutral: "bg-[#F3F3F6] text-[#374151] hover:bg-[#E9E9EE]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:opacity-60 sm:flex-1 ${styles[variant]}`}
    >
      {loading ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />} {label}
    </button>
  );
}

function PostDialog({ post, paused, onClose, onDelete }) {
  const closeRef = useRef(null);
  useDialog(!!post && !paused, onClose);
  useEffect(() => { if (post) closeRef.current?.focus(); }, [post]);
  if (!post) return null;
  const likes = post.likes?.length || 0;

  return (
    <DialogFrame labelledBy="post-dialog-title" onClose={onClose} maxWidth={680}>
      <DialogHeader onClose={onClose} closeRef={closeRef}>
        <div className="flex items-center gap-3">
          <Avatar src={getAvatarUrl(post.userId)} size={52} className="border-2 border-[#ffffff33]" />
          <div className="min-w-0">
            <h2 id="post-dialog-title" className="m-0 text-lg font-medium truncate" style={FONT_HEADING}>{getAuthorName(post.userId)}</h2>
            <p className="m-0 text-xs text-[#ffffffb3]">{roleLabel(post.userId?.role)} · {formatDate(post.createdAt, true)}</p>
          </div>
        </div>
      </DialogHeader>

      <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <TypeBadge type={post.postType} />
          <span className="inline-flex items-center gap-1 rounded-full bg-[#fff] border border-[#EEEEF1] px-2.5 py-1 text-[11px] font-semibold text-[#374151]">
            <Heart size={11} className="text-[#EA352B] fill-[#EA352B]" /> {likes} {likes === 1 ? "like" : "likes"}
          </span>
          <span className="text-[11px] text-[#9CA3AF]">{fromNow(post.createdAt)}</span>
        </div>

        <DialogSection title="Post">
          <p className="m-0 text-[15px] text-[#374151] leading-relaxed whitespace-pre-line">
            {post.description || <span className="text-[#9CA3AF] italic">No text</span>}
          </p>
          {post.image && (
            <div className="mt-4 rounded-2xl overflow-hidden border border-[#EEEEF1] bg-[#F7F7F9]">
              <PostImage src={post.image} className="w-full max-h-[360px]" />
            </div>
          )}
        </DialogSection>

        {post.tags?.length > 0 && (
          <DialogSection title="Tags">
            <div className="flex gap-2 flex-wrap">
              {post.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#F7F7F9] px-3 py-1 text-xs text-[#374151]"><TagIcon size={11} className="text-[#EA352B]" /> {t}</span>
              ))}
            </div>
          </DialogSection>
        )}
      </div>

      <DialogFooter>
        <ActionBtn Icon={X} label="Close" onClick={onClose} variant="outline" />
        <ActionBtn Icon={Trash2} label="Delete post" onClick={onDelete} variant="danger" />
      </DialogFooter>
    </DialogFrame>
  );
}

function EventDialog({ event: e, paused, busy, onClose, onApprove, onReject, onDelete }) {
  const closeRef = useRef(null);
  useDialog(!!e && !paused, onClose);
  useEffect(() => { if (e) closeRef.current?.focus(); }, [e]);
  if (!e) return null;

  const key = eventStatusKey(e.status);
  const when = moment(e.eventDateTime);
  const going = Array.isArray(e.going) ? e.going : [];
  const goingUsers = going.filter((g) => g && typeof g === "object");
  const image = getEventImage(e);
  const mapsUrl = e.location?.text ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.location.text)}` : null;

  return (
    <DialogFrame labelledBy="event-dialog-title" onClose={onClose} maxWidth={760}>
      <DialogHeader onClose={onClose} closeRef={closeRef}>
        <div className="flex items-center gap-4">
          <DateTile value={e.eventDateTime} large />
          <div className="min-w-0">
            <EventStatusBadge status={e.status} />
            <h2 id="event-dialog-title" className="m-0 mt-2 text-2xl sm:text-xl font-medium" style={FONT_HEADING}>{e.eventName || "Untitled event"}</h2>
            <p className="m-0 mt-1 text-xs text-[#ffffffb3] flex items-center gap-1.5">
              <Avatar src={getAvatarUrl(e.userId, { forceDefaultForServiceProvider: true })} size={18} />
              Organized by {getAuthorName(e.userId)}
            </p>
          </div>
        </div>
      </DialogHeader>

      <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-4 space-y-4">
        {key === "pending" && (
          <div className="flex items-center gap-3 rounded-2xl bg-[#FFFBEB] border border-[#FDE68A] px-4 py-3">
            <Megaphone size={18} className="text-[#B45309] shrink-0" />
            <p className="m-0 text-sm text-[#78350F]">This event isn’t visible in the app yet. Approve it to make it live, or reject it.</p>
          </div>
        )}

        {image && (
          <div className="rounded-2xl overflow-hidden border border-[#EEEEF1] bg-[#F7F7F9]">
            <PostImage src={image} className="w-full max-h-[280px]" />
          </div>
        )}

        <div className="grid grid-cols-3 sm:grid-cols-1 gap-3">
          <InfoTile Icon={CalendarClock} label="Date & time" value={when.isValid() ? when.format("ddd, DD MMM YYYY") : "—"} sub={when.isValid() ? `${when.format("hh:mm A")} · ${when.fromNow()}` : ""} />
          <InfoTile
            Icon={MapPin}
            label="Location"
            value={e.location?.text || "No location"}
            sub={mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="no-underline inline-flex items-center gap-1 text-[#EA352B] font-semibold">
                Open in Maps <ExternalLink size={11} />
              </a>
            )}
          />
          <InfoTile Icon={Users} label="Going" value={`${going.length} ${going.length === 1 ? "person" : "people"}`} sub={`Created ${fromNow(e.createdAt)}`} />
        </div>

        <DialogSection title="About this event">
          <p className="m-0 text-sm text-[#374151] leading-relaxed whitespace-pre-line">
            {e.description || <span className="text-[#9CA3AF] italic">No description</span>}
          </p>
        </DialogSection>

        {goingUsers.length > 0 && (
          <DialogSection title={`Attendees · ${going.length}`}>
            <div className="flex flex-wrap gap-2">
              {goingUsers.slice(0, 12).map((u, i) => (
                <span key={u._id || i} className="inline-flex items-center gap-2 rounded-full bg-[#F7F7F9] pl-1 pr-3 py-1 text-xs text-[#374151]">
                  <Avatar src={getAvatarUrl(u)} size={22} /> {getAuthorName(u)}
                </span>
              ))}
              {going.length > 12 && <span className="self-center text-xs text-[#9CA3AF]">+{going.length - 12} more</span>}
            </div>
          </DialogSection>
        )}

        {e.tags?.length > 0 && (
          <DialogSection title="Tags">
            <div className="flex gap-2 flex-wrap">
              {e.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#F7F7F9] px-3 py-1 text-xs text-[#374151]"><TagIcon size={11} className="text-[#EA352B]" /> {t}</span>
              ))}
            </div>
          </DialogSection>
        )}
      </div>

      <DialogFooter>
        {key === "pending" && (
          <>
            <ActionBtn Icon={Ban} label="Reject" onClick={onReject} variant="outline" loading={busy === `reject:${e._id}`} disabled={!!busy} />
            <ActionBtn Icon={CheckCircle2} label="Approve & publish" onClick={onApprove} variant="success" loading={busy === `approve:${e._id}`} disabled={!!busy} />
          </>
        )}
        {key === "live" && (
          <>
            <ActionBtn Icon={RotateCcw} label="Revoke approval" onClick={onReject} variant="outline" loading={busy === `reject:${e._id}`} disabled={!!busy} />
            <ActionBtn Icon={Trash2} label="Delete event" onClick={onDelete} variant="danger" disabled={!!busy} />
          </>
        )}
        {key === "rejected" && (
          <>
            <ActionBtn Icon={Trash2} label="Delete" onClick={onDelete} variant="outline" disabled={!!busy} />
            <ActionBtn Icon={CheckCircle2} label="Approve" onClick={onApprove} variant="success" loading={busy === `approve:${e._id}`} disabled={!!busy} />
          </>
        )}
      </DialogFooter>
    </DialogFrame>
  );
}

function InfoTile({ Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-[#fff] border border-[#EEEEF1] p-4 min-w-0">
      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#9CA3AF]">
        <Icon size={12} className="text-[#EA352B]" /> {label}
      </span>
      <p className="m-0 mt-1.5 text-sm font-semibold text-[#111]" title={typeof value === "string" ? value : undefined}>{value}</p>
      {sub && <p className="m-0 mt-0.5 text-[11px] text-[#9CA3AF]">{sub}</p>}
    </div>
  );
}

function ConfirmDelete({ target, busy, onCancel, onConfirm }) {
  useDialog(!!target && !busy, onCancel);
  if (!target) return null;
  return (
    <DialogFrame labelledBy="confirm-title" onClose={busy ? () => {} : onCancel} maxWidth={420}>
      <div className="p-6 bg-[#fff]">
        <span className="w-14 h-14 rounded-2xl bg-[#FDECEA] flex items-center justify-center">
          <Trash2 size={24} className="text-[#EA352B]" />
        </span>
        <h3 id="confirm-title" className="m-0 mt-4 text-lg font-medium text-[#111]" style={FONT_HEADING}>Delete this {target.type}?</h3>
        <p className="m-0 mt-2 text-sm text-[#6B7280] leading-relaxed">
          {target.title && <><b className="text-[#111]">{target.title}</b> — </>}
          this will be removed from the app permanently. This can’t be undone.
        </p>
        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onCancel} disabled={busy} className="flex-1 rounded-xl border border-[#EEEEF1] py-2.5 text-sm font-semibold text-[#374151] hover:bg-[#F7F7F9] transition disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#EA352B] py-2.5 text-sm font-semibold text-[#fff] hover:bg-[#D42F26] transition disabled:opacity-70">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete
          </button>
        </div>
      </div>
    </DialogFrame>
  );
}
